import { useState, useCallback, useEffect } from 'react';
import { ReactReader, ReactReaderStyle } from 'react-reader';
import type { Rendition } from 'epubjs';
import { createClient } from '@supabase/supabase-js';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { hasEpubAccess } from '../../utils/subscription';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SEO from '../../components/SEO';
import { Lock, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function EpubReader() {
    const { t, i18n } = useTranslation();
    const { theme } = useTheme();
    const [location, setLocation] = useState<string | number>(0);
    const [user, setUser] = useState<any>(null);
    const [subscription, setSubscription] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [renditionRef, setRenditionRef] = useState<Rendition | null>(null);

    const epubUrl = i18n.language === 'en' ? '/discover-canada-en.epub' : '/discover-canada.epub';

    // Fetch subscription
    const fetchSubscription = useCallback(async (userId: string) => {
        try {
            const { data: subs } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', userId)
                .eq('status', 'active');

            let effectiveSub = null;
            if (subs && subs.length > 0) {
                effectiveSub = subs.find((s: any) => s.plan_name === 'Premium') || subs[0];
            }
            setSubscription(effectiveSub);
        } catch (error) {
            console.error('Error fetching subscription:', error);
        }
    }, []);

    // Check auth
    const checkAuth = useCallback(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            window.REACT_APP_NAVIGATE('/login');
            return;
        }
        setUser(user);
        await fetchSubscription(user.id);
        setLoading(false);
    }, [fetchSubscription]);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Apply theme styles whenever rendition or theme changes
    useEffect(() => {
        if (renditionRef) {
            const isDark = theme === 'dark';
            renditionRef.themes.register('custom', {
                'body': {
                    color: isDark ? '#e4e4e7 !important' : '#1a1a2e !important',
                    background: isDark ? '#1E1E2E !important' : '#ffffff !important',
                    'max-width': '100% !important',
                    padding: '8px !important',
                    'overflow-x': 'hidden !important',
                    'word-wrap': 'break-word !important',
                    'overflow-wrap': 'break-word !important',
                },
                'p, span, div, li, td, th, h1, h2, h3, h4, h5, h6': {
                    color: isDark ? '#e4e4e7 !important' : '#1a1a2e !important',
                    'max-width': '100% !important',
                },
                'img': {
                    'max-width': '100% !important',
                    height: 'auto !important',
                },
                'table': {
                    'max-width': '100% !important',
                    'table-layout': 'fixed !important',
                    'word-break': 'break-word !important',
                },
                'pre, code': {
                    'white-space': 'pre-wrap !important',
                    'word-wrap': 'break-word !important',
                    'max-width': '100% !important',
                    'overflow-x': 'hidden !important',
                },
                '*': {
                    'max-width': '100% !important',
                    'box-sizing': 'border-box !important',
                },
                'a': {
                    color: isDark ? '#60a5fa !important' : '#2563eb !important',
                },
            });
            renditionRef.themes.select('custom');
            renditionRef.spread('none');
        }
    }, [renditionRef, theme]);

    const getRendition = useCallback((rendition: Rendition) => {
        setRenditionRef(rendition);
        rendition.spread('none');
    }, []);

    const canAccess = hasEpubAccess(subscription);

    // Loading state
    if (loading) {
        return (
            <DashboardLayout user={user}>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </DashboardLayout>
        );
    }

    // No access — redirect to pricing
    if (!canAccess) {
        return (
            <DashboardLayout user={user}>
                <SEO noindex={true} />
                <div className="max-w-2xl mx-auto text-center py-20">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-primary/10 text-primary mb-6">
                        <BookOpen size={40} />
                    </div>
                    <h1 className="text-3xl font-bold text-text-main mb-4">
                        {t('dashboard.epub.locked_title', { defaultValue: 'Accès réservé aux abonnés' })}
                    </h1>
                    <p className="text-text-muted mb-8 max-w-md mx-auto">
                        {t('dashboard.epub.locked_desc', { defaultValue: 'Souscrivez à un abonnement Standard ou Premium pour accéder au livre au format EPUB.' })}
                    </p>
                    <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-all shadow-lg shadow-primary/20">
                        {t('dashboard.epub.locked_cta', { defaultValue: 'Voir les offres' })}
                        <Lock size={18} />
                    </Link>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout user={user}>
            <SEO noindex={true} />
            <div className="flex flex-col h-full bg-dark-bg text-text-main rounded-2xl border border-dark-border overflow-hidden shadow-lg p-2 sm:p-4">
                <div className="mb-3 sm:mb-4 px-1">
                    <h1 className="text-xl sm:text-2xl font-bold text-text-main">
                        {t('dashboard.nav.epub') || 'Livre EPUB'}
                    </h1>
                    <p className="text-text-muted mt-1 text-sm sm:text-base">
                        {t('dashboard.epub.subtitle') || 'Suivez le contenu de la version EPUB.'}
                    </p>
                </div>
                
                <div
                    className="flex-1 relative border border-dark-border rounded-xl bg-dark-card overflow-hidden epub-reader-container"
                    style={{ minHeight: 'calc(100vh - 220px)' }}
                >
                    <ReactReader
                        url={epubUrl}
                        title={t('dashboard.nav.epub') || 'Livre EPUB'}
                        location={location}
                        locationChanged={(epubcifi: string) => setLocation(epubcifi)}
                        getRendition={getRendition}
                        readerStyles={theme === 'dark' ? darkReaderTheme : lightReaderTheme}
                        epubOptions={{
                            allowScriptedContent: true,
                            spread: 'none',
                        }}
                    />
                </div>
            </div>
        </DashboardLayout>
    );
}

const lightReaderTheme: typeof ReactReaderStyle = {
    ...ReactReaderStyle,
    container: {
        ...ReactReaderStyle.container,
        overflow: 'hidden',
        height: '100%',
        width: '100%',
    },
    readerArea: {
        ...ReactReaderStyle.readerArea,
        overflow: 'hidden',
    },
    reader: {
        ...ReactReaderStyle.reader,
        overflow: 'hidden',
    },
    arrow: {
        ...ReactReaderStyle.arrow,
        minWidth: '30px',
        padding: '0',
        fontSize: '24px',
    },
};

const darkReaderTheme: typeof ReactReaderStyle = {
    ...ReactReaderStyle,
    container: {
        ...ReactReaderStyle.container,
        overflow: 'hidden',
        height: '100%',
        width: '100%',
        backgroundColor: '#1E1E2E',
    },
    readerArea: {
        ...ReactReaderStyle.readerArea,
        overflow: 'hidden',
        backgroundColor: '#1E1E2E',
    },
    reader: {
        ...ReactReaderStyle.reader,
        overflow: 'hidden',
    },
    titleArea: {
        ...ReactReaderStyle.titleArea,
        color: '#e4e4e7',
        backgroundColor: '#1E1E2E',
    },
    tocArea: {
        ...ReactReaderStyle.tocArea,
        backgroundColor: '#1E1E2E',
        color: '#fff',
    },
    tocButtonExpanded: {
        ...ReactReaderStyle.tocButtonExpanded,
        backgroundColor: '#303046',
    },
    tocButton: {
        ...ReactReaderStyle.tocButton,
        backgroundColor: '#303046',
        color: '#fff',
    },
    arrow: {
        ...ReactReaderStyle.arrow,
        color: '#e4e4e7',
        minWidth: '30px',
        padding: '0',
        fontSize: '24px',
    },
};
